import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useChatData } from '../context/ChatContext';
import { useAuth } from '../../web3/context/AuthContext';
import { useSendPayment } from '../hooks/useSendPayment';
import {
  encodePaymentMessage,
  encodePaymentRequest,
  type PaymentMessagePayload,
  type PaymentRequestPayload,
} from '../types/paymentTypes';
import { ChainsObjectsArr } from '../../../chainsStaticObject/chainsStaticData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface SendMoneyModalProps {
  show: boolean;
  onHide: () => void;
  conversationId: string;
  recipientAddress?: string;
  groupMembers?: string[];
  prefill?: PaymentRequestPayload;
}

type SendStep = 'amount' | 'confirm' | 'sending';
type Mode = 'send' | 'request';

interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  balance: string;
  logo?: string;
}

const SendMoneyModal: React.FC<SendMoneyModalProps> = ({
  show,
  onHide,
  conversationId,
  recipientAddress: recipientAddressProp,
  groupMembers: groupMembersProp,
  prefill,
}) => {
  const { sendMessage, conversations } = useChatData();
  const { entityInfo, network } = useAuth();

  // Resolve recipient from conversation data when not provided via props
  const conversation = conversations.find((c: any) => c.id === conversationId);
  const recipientAddress = recipientAddressProp || conversation?.peerAddress || '';
  const groupMembers = groupMembersProp || (conversation?.isGroup ? conversation.members : undefined);
  const {
    sendPayment,
    availableChains,
    fetchTokens,
    isCustodial,
    walletAddress,
  } = useSendPayment();

  const [mode, setMode] = useState<Mode>(prefill ? 'send' : 'send');
  const [step, setStep] = useState<SendStep>('amount');
  const [selectedChain, setSelectedChain] = useState<any>(null);
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [selectedToken, setSelectedToken] = useState<TokenInfo | null>(null);
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [selectedMember, setSelectedMember] = useState('');
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [sendingStatus, setSendingStatus] = useState('');

  // Pre-select current chain on open
  useEffect(() => {
    if (show) {
      if (prefill) {
        const chain = availableChains.find((c: any) => c.chain === prefill.chain);
        if (chain) setSelectedChain(chain);
        setAmount(prefill.amount);
        setMode('send');
      } else {
        const currentChain = availableChains.find(
          (c: any) => c.chain === network?.chain
        );
        setSelectedChain(currentChain || availableChains[0] || null);
      }
    }
  }, [show, network, availableChains, prefill]);

  // Fetch tokens when chain changes
  useEffect(() => {
    if (!selectedChain || !show) return;
    let cancelled = false;

    setIsLoadingTokens(true);
    setTokens([]);
    setSelectedToken(null);

    fetchTokens(selectedChain.chain).then((result) => {
      if (cancelled) return;
      setTokens(result);
      // Pre-select native token or prefill token
      if (prefill) {
        const match = result.find((t) => t.address === prefill.tokenAddress);
        setSelectedToken(match || result[0] || null);
      } else {
        setSelectedToken(result[0] || null);
      }
      setIsLoadingTokens(false);
    });

    return () => { cancelled = true; };
  }, [selectedChain, show, fetchTokens, prefill]);

  // Set default member for groups
  useEffect(() => {
    if (recipientAddress) {
      setSelectedMember(recipientAddress);
    } else if (groupMembers?.length) {
      setSelectedMember(groupMembers[0]);
    }
  }, [recipientAddress, groupMembers]);

  const resetModal = useCallback(() => {
    setStep('amount');
    setMode('send');
    setAmount('');
    setPassword('');
    setNote('');
    setSendingStatus('');
    setSelectedToken(null);
  }, []);

  const handleClose = useCallback(() => {
    resetModal();
    onHide();
  }, [onHide, resetModal]);

  const effectiveRecipient = groupMembers ? selectedMember : recipientAddress || '';

  const amountNum = parseFloat(amount) || 0;
  const balanceNum = parseFloat(selectedToken?.balance || '0');
  const insufficientBalance = amountNum > balanceNum;
  const canProceed =
    amountNum > 0 &&
    selectedToken &&
    selectedChain &&
    effectiveRecipient &&
    !insufficientBalance;

  const handleSend = useCallback(async () => {
    if (!canProceed || !selectedToken || !selectedChain) return;

    setStep('sending');
    setSendingStatus('Awaiting signature...');

    let txHash: string;

    // Step 1: Execute on-chain transfer
    try {
      const result = await sendPayment(
        effectiveRecipient,
        amount,
        selectedToken.address,
        selectedChain.chain,
        selectedToken.decimals,
        isCustodial ? password : undefined,
      );
      txHash = result.txHash;
    } catch (err: any) {
      console.error('[SendMoney] Transfer failed:', err);
      toast.error(err?.message || 'Transaction failed');
      setStep('confirm');
      setSendingStatus('');
      return;
    }

    // Step 2: Send XMTP message (tx already succeeded — retry on failure)
    setSendingStatus('Sending message...');

    const payload: PaymentMessagePayload = {
      version: 1,
      txHash,
      chain: selectedChain.chain,
      chainId: Number(selectedChain.networkId),
      tokenSymbol: selectedToken.symbol,
      tokenAddress: selectedToken.address,
      amount,
      decimals: selectedToken.decimals,
      senderAddress: walletAddress,
      recipientAddress: effectiveRecipient,
      status: 'confirmed',
      timestamp: Date.now(),
    };

    const encoded = encodePaymentMessage(payload);
    let messageSent = false;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await sendMessage(conversationId, encoded);
        messageSent = true;
        break;
      } catch (err: any) {
        console.warn(`[SendMoney] XMTP message attempt ${attempt + 1} failed:`, err?.message);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    }

    if (messageSent) {
      toast.success('Payment sent!');
    } else {
      toast.success('Payment confirmed on-chain, but chat message failed to send.');
    }
    handleClose();
  }, [
    canProceed, selectedToken, selectedChain, effectiveRecipient,
    amount, password, isCustodial, walletAddress, sendPayment,
    sendMessage, conversationId, handleClose,
  ]);

  const handleRequest = useCallback(async () => {
    if (!selectedToken || !selectedChain || amountNum <= 0) return;

    const payload: PaymentRequestPayload = {
      version: 1,
      chain: selectedChain.chain,
      chainId: Number(selectedChain.networkId),
      tokenSymbol: selectedToken.symbol,
      tokenAddress: selectedToken.address,
      amount,
      decimals: selectedToken.decimals,
      requesterAddress: walletAddress,
      note: note.trim() || undefined,
      timestamp: Date.now(),
    };

    try {
      await sendMessage(conversationId, encodePaymentRequest(payload));
      toast.success('Payment request sent!');
      handleClose();
    } catch (err: any) {
      toast.error('Failed to send request');
    }
  }, [selectedToken, selectedChain, amount, amountNum, note, walletAddress, sendMessage, conversationId, handleClose]);

  const handleMaxClick = () => {
    if (selectedToken) {
      setAmount(selectedToken.balance);
    }
  };

  const shortenAddr = (addr: string) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  return (
    <Modal show={show} onHide={handleClose} centered className="chat-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {step !== 'amount' && mode === 'send' && (
            <button
              className="chat-btn chat-btn--icon"
              onClick={() => setStep(step === 'sending' ? 'confirm' : 'amount')}
              style={{ marginRight: 8 }}
              disabled={step === 'sending'}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}
          {mode === 'send' ? 'Send Money' : 'Request Money'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Mode tabs — hidden when prefill (always send mode) */}
        {!prefill && step === 'amount' && (
          <div className="chat-payment-modal__tabs">
            <button
              className={`chat-payment-modal__tab ${mode === 'send' ? 'chat-payment-modal__tab--active' : ''}`}
              onClick={() => setMode('send')}
            >
              Send
            </button>
            <button
              className={`chat-payment-modal__tab ${mode === 'request' ? 'chat-payment-modal__tab--active' : ''}`}
              onClick={() => setMode('request')}
            >
              Request
            </button>
          </div>
        )}

        {/* Step 1: Amount */}
        {step === 'amount' && (
          <div className="chat-payment-modal__form">
            {/* Chain selector */}
            <div className="chat-modal__field">
              <label className="chat-modal__label">Network</label>
              <select
                className="chat-search-input"
                value={selectedChain?.chain || ''}
                onChange={(e) => {
                  const chain = availableChains.find((c: any) => c.chain === e.target.value);
                  setSelectedChain(chain);
                }}
              >
                {availableChains.map((c: any) => (
                  <option key={c.chain} value={c.chain}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Token picker */}
            <div className="chat-modal__field">
              <label className="chat-modal__label">Token</label>
              {isLoadingTokens ? (
                <div className="chat-payment-modal__loading">
                  <Spinner animation="border" size="sm" /> Loading tokens...
                </div>
              ) : (
                <select
                  className="chat-search-input"
                  value={selectedToken?.address || ''}
                  onChange={(e) => {
                    const token = tokens.find((t) => t.address === e.target.value);
                    setSelectedToken(token || null);
                  }}
                >
                  {tokens.map((t) => (
                    <option key={t.address} value={t.address}>
                      {t.symbol} — Balance: {parseFloat(t.balance).toFixed(4)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Amount input */}
            <div className="chat-modal__field">
              <label className="chat-modal__label">Amount</label>
              <div className="chat-payment-modal__amount-row">
                <input
                  type="number"
                  className="chat-search-input chat-payment-modal__amount-input"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0"
                  step="any"
                />
                <button
                  className="chat-btn chat-btn--secondary chat-payment-modal__max-btn"
                  onClick={handleMaxClick}
                  disabled={!selectedToken}
                >
                  Max
                </button>
              </div>
              {insufficientBalance && amountNum > 0 && (
                <div className="chat-payment-modal__error">Insufficient balance</div>
              )}
            </div>

            {/* Group member picker */}
            {groupMembers && groupMembers.length > 0 && mode === 'send' && (
              <div className="chat-modal__field">
                <label className="chat-modal__label">Send to</label>
                <select
                  className="chat-search-input"
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                >
                  {groupMembers.map((addr) => (
                    <option key={addr} value={addr}>
                      {shortenAddr(addr)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Note field (request mode only) */}
            {mode === 'request' && (
              <div className="chat-modal__field">
                <label className="chat-modal__label">Note (optional)</label>
                <input
                  type="text"
                  className="chat-search-input"
                  placeholder="e.g. For lunch"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 100))}
                  maxLength={100}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 2: Confirm (send mode only) */}
        {step === 'confirm' && mode === 'send' && (
          <div className="chat-payment-modal__confirm">
            <div className="chat-payment-modal__summary">
              <div className="chat-payment-modal__summary-amount">
                {amount} {selectedToken?.symbol}
              </div>
              <div className="chat-payment-modal__summary-detail">
                on {selectedChain?.title}
              </div>
              <div className="chat-payment-modal__summary-detail">
                <FontAwesomeIcon icon={faArrowRight} />{' '}
                {shortenAddr(effectiveRecipient)}
              </div>
            </div>

            {isCustodial && (
              <div className="chat-modal__field">
                <label className="chat-modal__label">Password</label>
                <input
                  type="password"
                  className="chat-search-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            {selectedToken && selectedToken.address !== '0x0000000000000000000000000000000000000000' && (
              <div className="chat-payment-modal__note">
                This will require a token approval transaction first.
              </div>
            )}
          </div>
        )}

        {/* Step 3: Sending */}
        {step === 'sending' && (
          <div className="chat-payment-modal__sending">
            <Spinner animation="border" />
            <div className="chat-payment-modal__sending-status">
              {sendingStatus || 'Processing...'}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 'amount' && mode === 'send' && (
          <button
            className="chat-btn chat-btn--primary"
            disabled={!canProceed}
            onClick={() => setStep('confirm')}
          >
            Continue
          </button>
        )}

        {step === 'amount' && mode === 'request' && (
          <button
            className="chat-btn chat-btn--primary"
            disabled={amountNum <= 0 || !selectedToken || !selectedChain}
            onClick={handleRequest}
          >
            Request
          </button>
        )}

        {step === 'confirm' && (
          <button
            className="chat-btn chat-btn--primary"
            onClick={handleSend}
            disabled={isCustodial && !password}
          >
            Confirm & Send
          </button>
        )}

        {step !== 'sending' && (
          <button className="chat-btn chat-btn--secondary" onClick={handleClose}>
            Cancel
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SendMoneyModal;
